/**
 * Menu Suggestions hook
 * Aggregates user weekly selections to provide meal popularity insights
 */

import { useQuery, useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getUsers } from '@/lib/api/users';
import { getWeeklySelections } from '@/lib/api/weekly-selections';
import type {
  MenuSuggestions,
  MealPopularity,
  UserSelectionSummary,
} from '@/types/api';
import { endOfWeek, startOfWeek } from 'date-fns';
import { formatDateForAPI } from '@/lib/utils/date';
import { menuSuggestionsKeys } from '@/lib/query-keys';

// Re-export for backwards compatibility
export { menuSuggestionsKeys };

export interface UseMenuSuggestionsOptions {
  /** Week start date (Monday) in YYYY-MM-DD format */
  weekStartDate: string;
  /** Whether to enable the query */
  enabled?: boolean;
}

export interface UseMenuSuggestionsReturn {
  /** Menu suggestions data */
  data: MenuSuggestions | undefined;
  /** Whether any query is loading */
  isLoading: boolean;
  /** Whether all queries succeeded */
  isError: boolean;
  /** Combined error from any failed query */
  error: Error | null;
}

/**
 * Hook to fetch and aggregate menu suggestions based on user selections
 *
 * Features:
 * - Fetches all users in parallel
 * - Fetches weekly selections for each user
 * - Aggregates data into meal popularity insights
 * - Calculates popularity scores
 * - Sorts meals by total selections
 * - Memoizes expensive calculations
 *
 * @example
 * ```tsx
 * function MenuSuggestionsPanel() {
 *   const weekStart = formatDateForAPI(getWeekStart(new Date()));
 *   const { data, isLoading, isError } = useMenuSuggestions({
 *     weekStartDate: weekStart,
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *   if (isError) return <ErrorMessage />;
 *
 *   return (
 *     <div>
 *       <h3>Popular Meals This Week</h3>
 *       {data.popularMeals.slice(0, 5).map(meal => (
 *         <MealPopularityCard key={meal.mealId} meal={meal} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useMenuSuggestions({
  weekStartDate,
  enabled = true,
}: UseMenuSuggestionsOptions): UseMenuSuggestionsReturn {
  // Fetch all users
  const {
    data: usersData,
    isLoading: isUsersLoading,
    isError: isUsersError,
    error: usersError,
  } = useQuery({
    queryKey: ['users'],
    queryFn: () => getUsers(),
    enabled,
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });

  // Fetch weekly selections for each user in parallel
  const selectionQueries = useQueries({
    queries:
      usersData?.users.map((user) => ({
        queryKey: ['weekly-selections', 'selections', { userId: user.id, weekStartDate }],
        queryFn: () =>
          getWeeklySelections({
            userId: user.id,
            weekStartDate,
          }),
        enabled: enabled && !!usersData,
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 30, // 30 minutes
      })) || [],
  });

  // Aggregate loading and error states
  const isSelectionsLoading = selectionQueries.some((query) => query.isLoading);
  const isSelectionsError = selectionQueries.some((query) => query.isError);
  const selectionsError = selectionQueries.find((query) => query.error)?.error;

  const isLoading = isUsersLoading || isSelectionsLoading;
  const isError = isUsersError || isSelectionsError;
  const error = (usersError || selectionsError) as Error | null;

  // Memoize the aggregated data
  const suggestions = useMemo<MenuSuggestions | undefined>(() => {
    // Don't compute if still loading or no data
    if (isLoading || !usersData || selectionQueries.length === 0) {
      return undefined;
    }

    // Check if all queries have data
    const allQueriesSucceeded = selectionQueries.every((query) => query.data);
    if (!allQueriesSucceeded) {
      return undefined;
    }

    const users = usersData.users;
    const weekEndDate = formatDateForAPI(
      endOfWeek(startOfWeek(new Date(weekStartDate), { weekStartsOn: 1 }), {
        weekStartsOn: 1,
      })
    );

    // Map to aggregate meal data
    const mealMap = new Map<
      number,
      {
        mealId: number;
        mealName: string;
        tags: any[];
        selectedByUsers: Array<{
          userId: number;
          userName: string;
          isOrganizer: boolean;
          selectionCount: number;
        }>;
        totalSelections: number;
      }
    >();

    // Map for user summaries
    const userSummaries: UserSelectionSummary[] = [];
    let totalSelectionsCount = 0;
    let totalUsersWithSelections = 0;

    // Process each user's selections
    selectionQueries.forEach((query, index) => {
      const user = users[index];
      const selectionsData = query.data;

      if (!selectionsData || selectionsData.selections.length === 0) {
        return;
      }

      totalUsersWithSelections++;

      const userSelectedMeals: Array<{
        mealId: number;
        mealName: string;
        selectionCount: number;
      }> = [];

      let userTotalMealsSelected = 0;

      selectionsData.selections.forEach((selection) => {
        const { meal, selectionCount } = selection;

        // Update meal popularity map
        if (!mealMap.has(meal.id)) {
          mealMap.set(meal.id, {
            mealId: meal.id,
            mealName: meal.name,
            tags: meal.tags,
            selectedByUsers: [],
            totalSelections: 0,
          });
        }

        const mealData = mealMap.get(meal.id)!;
        mealData.selectedByUsers.push({
          userId: user.id,
          userName: user.name,
          isOrganizer: user.isOrganizer,
          selectionCount,
        });
        mealData.totalSelections += selectionCount;

        // Update user summary
        userSelectedMeals.push({
          mealId: meal.id,
          mealName: meal.name,
          selectionCount,
        });

        userTotalMealsSelected += selectionCount;
        totalSelectionsCount += selectionCount;
      });

      // Add user summary
      userSummaries.push({
        userId: user.id,
        userName: user.name,
        isOrganizer: user.isOrganizer,
        totalMealsSelected: userTotalMealsSelected,
        selectedMeals: userSelectedMeals,
      });
    });

    // Convert meal map to array and calculate popularity scores
    const popularMeals: MealPopularity[] = Array.from(mealMap.values())
      .map((meal) => ({
        ...meal,
        popularityScore:
          totalUsersWithSelections > 0
            ? (meal.totalSelections / totalUsersWithSelections) * 100
            : 0,
      }))
      .sort((a, b) => b.totalSelections - a.totalSelections);

    return {
      weekStartDate,
      weekEndDate,
      popularMeals,
      userSelectionsSummary: userSummaries,
      totalUsersWithSelections,
      totalSelectionsCount,
    };
  }, [isLoading, usersData, selectionQueries, weekStartDate]);

  return {
    data: suggestions,
    isLoading,
    isError,
    error,
  };
}
